import { Injectable, inject } from '@angular/core';
import { BaseNotifier } from '../../../../shared/notifiers/base-notifier';
import { Ref } from '../../../../shared/providers/ref';
import { Configuration } from '../../../../shared/configuration/configuration';
import { AboutInfo, AboutState } from './about-state';

/**
 * Ficha de marca del diálogo "Acerca de".
 *
 * Marca visible: MIDAGRI. El sistema es una consola institucional para la toma
 * de decisiones en el marco del Fenómeno El Niño (FEN).
 */
const ABOUT_INFO: AboutInfo = {
  appName: 'Sistema de toma de decisiones - FEN',
  version: '1.0.0',
  subtitle: 'MIDAGRI · Fenómeno El Niño',
  entity: 'MIDAGRI',
  department: 'Ministerio de Desarrollo Agrario y Riego — gob.pe/midagri',
  description:
    'Consola de decisión territorial: convierte datos geoespaciales oficiales en decisiones accionables. Vigila los avisos hidrometeorológicos en tiempo real, cruza su impacto sobre la superficie agrícola y la población pecuaria, y entrega recomendaciones por zona.',
  dataSources: [
    {
      name: 'SENAMHI',
      desc: 'Avisos meteorológicos oficiales en tiempo real y pronóstico de corto plazo (Nowcasting 1 h / 2 h / 24 h).',
    },
    {
      name: 'SERFOR',
      desc: 'Incendios forestales y focos de calor (últimas 24 h) para el seguimiento del territorio.',
    },
    {
      name: 'Cartografía base oficial',
      desc: 'Límites político-administrativos, centros poblados y cobertura temática de referencia territorial.',
    },
    {
      name: 'Cifras y padrones agrarios',
      desc: 'Superficie agrícola, cultivos priorizados y población pecuaria expuesta según severidad N2 / N3 / N4.',
    },
  ],
  copyright: '© MIDAGRI — Todos los derechos reservados.',
};

const DEFAULT_STATE: AboutState = {
  visible: false,
  info: ABOUT_INFO,
};

@Injectable()
export class AboutNotifier extends BaseNotifier<AboutState> {
  constructor(ref: Ref) {
    super(ref, DEFAULT_STATE);
    // Tras super(): sincronizamos la versión del entorno aquí para no
    // depender del orden de inicialización de campos dentro de build().
    this.syncVersion();
  }

  open(): void {
    this.updateState((s) => (s.visible ? s : { ...s, visible: true }));
  }

  close(): void {
    this.updateState((s) => (s.visible ? { ...s, visible: false } : s));
  }

  toggle(): void {
    this.updateState((s) => ({ ...s, visible: !s.visible }));
  }

  /** Ajusta la versión mostrada con la configurada en el entorno. */
  private syncVersion(): void {
    const version = this.resolveVersion();
    if (version === DEFAULT_STATE.info.version) return;

    this.updateState((s) => ({ ...s, info: { ...s.info, version } }));
  }

  /** Versión de `assets/env.js`; cae al valor por defecto si no está disponible. */
  private resolveVersion(): string {
    try {
      return inject(Configuration).version || DEFAULT_STATE.info.version;
    } catch {
      return DEFAULT_STATE.info.version;
    }
  }
}

